/**
 * useNotifications Hook
 * Custom React Hook สำหรับจัดการ Notification State และ API Calls
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { notificationService } from '../services/notificationService'
import { Notification } from '../types/notification'
import { useAuth } from '../contexts/AuthContext'

// Polling interval: 30 วินาที
const POLLING_INTERVAL = 30000

interface UseNotificationsReturn {
  // State
  notifications: Notification[]
  unreadCount: number
  loading: boolean
  error: string | null

  // Actions
  fetchNotifications: (page?: number, perPage?: number, unreadOnly?: boolean) => Promise<void>
  fetchUnreadCount: () => Promise<void>
  markAsRead: (notificationId: string) => Promise<void>
  markAllAsRead: () => Promise<void>
  deleteNotification: (notificationId: string) => Promise<void>
  refreshNotifications: () => Promise<void>
  clearError: () => void
}

export const useNotifications = (): UseNotificationsReturn => {
  const { isAuthenticated } = useAuth()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Ref เพื่อเก็บ interval ID
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)

  /**
   * ดึงรายการ notifications
   */
  const fetchNotifications = useCallback(async (
    page: number = 1,
    perPage: number = 10,
    unreadOnly: boolean = false
  ) => {
    if (!isAuthenticated) {
      setNotifications([])
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await notificationService.getMyNotifications(page, perPage, unreadOnly)
      setNotifications(response.notifications)
    } catch (err: any) {
      const errorMessage = err.message || 'ไม่สามารถโหลด notifications ได้'
      setError(errorMessage)
      console.error('Error fetching notifications:', err)
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  /**
   * ดึงจำนวน unread notifications
   */
  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) {
      setUnreadCount(0)
      return
    }

    try {
      const response = await notificationService.getUnreadCount()
      setUnreadCount(response.unread_count)
    } catch (err: any) {
      console.error('Error fetching unread count:', err)
      // ไม่ set error เพราะเป็น background task
    }
  }, [isAuthenticated])

  /**
   * Mark notification เป็น read
   */
  const markAsRead = useCallback(async (notificationId: string) => {
    if (!isAuthenticated) return

    try {
      await notificationService.markAsRead(notificationId)

      // อัปเดต local state
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId
            ? { ...notif, is_read: true, read_at: new Date().toISOString() }
            : notif
        )
      )

      // อัปเดต unread count
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch (err: any) {
      const errorMessage = err.message || 'ไม่สามารถทำเครื่องหมายว่าอ่านแล้วได้'
      setError(errorMessage)
      console.error('Error marking notification as read:', err)
    }
  }, [isAuthenticated])

  /**
   * Mark ทุก notifications เป็น read
   */
  const markAllAsRead = useCallback(async () => {
    if (!isAuthenticated) return

    try {
      await notificationService.markAllAsRead()

      // อัปเดต local state
      const now = new Date().toISOString()
      setNotifications(prev =>
        prev.map(notif => ({
          ...notif,
          is_read: true,
          read_at: now
        }))
      )

      // รีเซ็ต unread count
      setUnreadCount(0)
    } catch (err: any) {
      const errorMessage = err.message || 'ไม่สามารถทำเครื่องหมายทั้งหมดว่าอ่านแล้วได้'
      setError(errorMessage)
      console.error('Error marking all notifications as read:', err)
    }
  }, [isAuthenticated])

  /**
   * ลบ notification
   */
  const deleteNotification = useCallback(async (notificationId: string) => {
    if (!isAuthenticated) return

    try {
      await notificationService.deleteNotification(notificationId)

      // ลบออกจาก local state
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId))

      // ถ้า notification นั้นยัง unread → ลด unread count
      const notification = notifications.find(n => n.id === notificationId)
      if (notification && !notification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1))
      }
    } catch (err: any) {
      const errorMessage = err.message || 'ไม่สามารถลบ notification ได้'
      setError(errorMessage)
      console.error('Error deleting notification:', err)
    }
  }, [isAuthenticated, notifications])

  /**
   * Refresh notifications และ unread count
   */
  const refreshNotifications = useCallback(async () => {
    await Promise.all([
      fetchNotifications(),
      fetchUnreadCount()
    ])
  }, [fetchNotifications, fetchUnreadCount])

  /**
   * ล้าง error message
   */
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  /**
   * Initial fetch เมื่อ component mount
   * FIX: ลบ refreshNotifications จาก dependencies เพื่อหลีกเลี่ยง infinite loop
   * เรียก fetchNotifications และ fetchUnreadCount โดยตรงแทน
   */
  useEffect(() => {
    if (isAuthenticated) {
      // เรียกโดยตรงแทนการใช้ refreshNotifications เพื่อหลีกเลี่ยง circular dependency
      fetchNotifications()
      fetchUnreadCount()
    } else {
      // Clear state เมื่อ logout
      setNotifications([])
      setUnreadCount(0)
      setError(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  /**
   * Setup polling สำหรับ unread count
   * Auto-refresh ทุก 30 วินาที
   */
  useEffect(() => {
    if (!isAuthenticated) {
      // Clear polling เมื่อไม่ได้ login
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
      return
    }

    // เริ่ม polling
    pollingIntervalRef.current = setInterval(() => {
      fetchUnreadCount()
    }, POLLING_INTERVAL)

    // Cleanup function
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }
  }, [isAuthenticated, fetchUnreadCount])

  return {
    // State
    notifications,
    unreadCount,
    loading,
    error,

    // Actions
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refreshNotifications,
    clearError
  }
}
